<template>
  <ul role="list" class="max-h-72 overflow-auto overscroll-contain rounded-b-lg w-full">
    <li
      v-for="document in props.documents"
      :key="document.name"
      class="flex justify-between sm:gap-x-6 p-3 mx-1 first:mt-2 last:mb-2 mb-3 bg-transparent rounded-lg ring-1 ring-gray-200 dark:ring-neutral-600"
    >
      <div class="flex min-w-0 gap-x-4 items-center">
        <img :src="getFileIconByType(document)" alt="PDF Icon" class="w-8 h-8" />
        <div class="min-w-0 flex flex-col items-start max-w-96 truncate">
          <div class="text-sm text-black dark:text-neutral-300 flex items-center">
            <div class="truncate">{{ document.name }}</div>
          </div>
          <div class="text-xs text-gray-500 dark:text-neutral-500 truncate mt-0.5 flex items-center">
            <div>{{ formatFileSize(document.size) }}</div>
            <div v-if="props.downloadable" class="border-l border-gray-300 h-3 mx-2"></div>
            <div v-if="props.downloadable" class="text-blue-800 flex items-center gap-1 cursor-pointer hover:text-blue-500 dark:text-blue-600 dark:hover:text-blue-500" @click="downloadFile(document)">
              <div class="text-xs">view</div>
              <ArrowTopRightOnSquareIcon
              v-if="props.downloadable"
              class="size-3.5"
            />
            </div>
          </div>
        </div>
      </div>
      <div class="self-top-0 flex flex-col items-end justify-between">
        <TrashIcon
          class="w-4 h-4 text-neutral-400 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-600 transition ease-in-out duration-00 cursor-pointer"
          @click="handleDeletIconClicked(document.name)"
        />
        <div v-if="document?.created_at" class="text-[10px] text-gray-500 font-light">{{ formatDate(document?.created_at) }}</div>
        <div v-else></div>
      </div>
    </li>
  </ul>
</template>

<script setup>
// Libraries
import { formatFileSize, formatDate } from "@/helper/helper";
import { TrashIcon, ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/outline";

// Icons
import PdfIcon from "@/assets/icons/PdfIcon.svg";
import PngIcon from "@/assets/icons/PngIcon.svg";
import JpgIcon from "@/assets/icons/JpgIcon.svg";
import TxtIcon from "@/assets/icons/TxtIcon.svg";
import DocIcon from "@/assets/icons/DocIcon.svg";
import CsvIcon from "@/assets/icons/CsvIcon.svg";
import XlsIcon from "@/assets/icons/XlsIcon.svg";
import IdkIcon from "@/assets/icons/IdkIcon.svg";

// Stores
import { useAccountsStore } from "@/stores/accounts";
const accountsStore = useAccountsStore();
import { usePartiesStore } from "@/stores/parties";
const partiesStore = usePartiesStore();
import { useOfferingsStore } from "@/stores/offerings";
const offeringsStore = useOfferingsStore();

// Props
const props = defineProps({
  documents: { type: Array, required: true }, // Expects an array of file objects (DataTransfer files array)
  downloadable: { type: Boolean, required: false, default: false }, // If the file is downloadable
  type: { type: String, required: false, default: "" }, // Type of file
  offering_id: { type: String, required: false, default: "" }, // Offering ID
});

// Emits
const emit = defineEmits(["remove"]);

// Handle delete
const handleDeletIconClicked = (document) => {
  emit("remove", document);
};

// Determine File Type
const getFileIconByType = (file) => {
  if (!file) return IdkIcon;
  let foundType = file.doc_type || file.type;
  if (!foundType) {
    if (!file.metadata?.mimetype) return IdkIcon;
    foundType = file.metadata.mimetype;
  }
  const typeMapping = {
    "application/pdf": PdfIcon,
    "image/jpeg": JpgIcon,
    "image/png": PngIcon,
    "text/plain": TxtIcon,
    "application/msword": DocIcon,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": DocIcon,
    "text/csv": CsvIcon,
    "application/vnd.ms-excel": XlsIcon,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": XlsIcon,
  };
  return typeMapping[foundType] || IdkIcon;
};

// Download File
async function downloadFile(file) {
  if (!props.downloadable) return
  if (!file) return
  if (props.type == "offering") {
    const url = offeringsStore.getPublicFileUrl(props.offering_id, `documents/${file.name}`);
    window.open(url, "_blank");
  }
  if (props.type == "accreditation") {
    if (!file.userFolder || !file.accountFolder || !file.name) return
    const url = await accountsStore.downloadAccreditationFile(`${file.userFolder}/accredited/${file.accountFolder}/${file.name}`);
    window.open(url, "_blank");
  }
  if (props.type == "verification") {
    if (!file.userFolder || !file.partyFolder || !file.name) return
    const url = await partiesStore.downloadInvestorFile(`${file.userFolder}/investor/${file.partyFolder}/${file.name}`);
    window.open(url, "_blank");
  }
}
</script>
